import React from 'react';

import { Alert } from '@mui/material';

import ApprovalQueue from './ApprovalQueue';
import PlaceholderChecklistTab from './PlaceholderChecklistTab';
import { getHiddenApprovedCount, isRowApproved } from './approvalQueueModel';
import BlackoutReportCard from './tabs/blackoutDates/BlackoutReportCard';
import type { BlackoutCoverageSummary } from './tabs/blackoutDates/types';
import BundledExperiencesReportCard from './tabs/bundledExperiences/BundledExperiencesReportCard';
import CampaignReportCard from './tabs/campaign/CampaignReportCard';
import DealOptionsInclusionsReportCard from './tabs/dealOptionsInclusions/DealOptionsInclusionsReportCard';
import type { DealOptionsInclusionsDealRow } from './tabs/dealOptionsInclusions/types';
import DiscountsMarginReportCard from './tabs/discountsMargin/DiscountsMarginReportCard';
import CapacityReportCard from './tabs/egs/CapacityReportCard';
import MarginReportCard from './tabs/egs/MarginReportCard';
import SetupReportCard from './tabs/setup/SetupReportCard';
import SurchargesReportCard from './tabs/surcharges/SurchargesReportCard';
import TaxesPaymentsReportCard from './tabs/taxesPayments/TaxesPaymentsReportCard';
import type {
  ApprovalQueueItem,
  ApprovalReportSummary,
  ChecklistCategory,
  ChecklistCategoryId,
  ChecklistRowsByCategory,
  ManualOverrideState,
} from './types';
import type { VisibleChecklistRows } from './useVisibleChecklistRows';

interface Props {
  activeCategory: ChecklistCategory;
  blackoutCoverage: BlackoutCoverageSummary;
  dealOptionsInclusionsDealRows: Array<DealOptionsInclusionsDealRow>;
  expandedQueueItems: Record<string, boolean>;
  isApprovalQueueMode: boolean;
  manualOverrides: ManualOverrideState;
  needsApprovalCount: number;
  opportunityError?: unknown;
  reportSummaries: Array<ApprovalReportSummary>;
  rows: ChecklistRowsByCategory;
  searchQuery: string;
  showAutoApproved: boolean;
  travelFrom?: string;
  travelTo?: string;
  visibleQueueItems: Array<ApprovalQueueItem>;
  visibleRows: VisibleChecklistRows;
  onApprovalChange: (rowId: string) => void;
  onSelectTab: (categoryId: ChecklistCategoryId) => void;
  onToggleDetails: (itemId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function ChecklistActiveTab({
  activeCategory,
  blackoutCoverage,
  dealOptionsInclusionsDealRows,
  expandedQueueItems,
  isApprovalQueueMode,
  manualOverrides,
  needsApprovalCount,
  opportunityError,
  reportSummaries,
  rows,
  searchQuery,
  showAutoApproved,
  travelFrom,
  travelTo,
  visibleQueueItems,
  visibleRows,
  onApprovalChange,
  onSelectTab,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  if (isApprovalQueueMode) {
    return (
      <ApprovalQueue
        expandedQueueItems={expandedQueueItems}
        items={visibleQueueItems}
        needsApprovalCount={needsApprovalCount}
        onApprovalChange={onApprovalChange}
        onSelectTab={onSelectTab}
        onToggleDetails={onToggleDetails}
        reportSummaries={reportSummaries}
      />
    );
  }

  switch (activeCategory.id) {
    case 'setup':
      return (
        <SetupReportCard
          expandedRows={expandedQueueItems}
          hiddenApprovedCount={getHiddenApprovedCount(rows.setupRows, manualOverrides)}
          manualOverrides={manualOverrides}
          rows={visibleRows.visibleSetupRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
          onToggleDetails={onToggleDetails}
          onUnapprovalChange={onUnapprovalChange}
        />
      );
    case 'campaign':
      return (
        <CampaignReportCard
          expandedRows={expandedQueueItems}
          hiddenApprovedCount={getHiddenApprovedCount(rows.campaignRows, manualOverrides)}
          manualOverrides={manualOverrides}
          rows={visibleRows.visibleCampaignRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
          onToggleDetails={onToggleDetails}
          onUnapprovalChange={onUnapprovalChange}
        />
      );
    case 'discountsMargin':
      return (
        <DiscountsMarginReportCard
          expandedRows={expandedQueueItems}
          hiddenApprovedCount={getHiddenApprovedCount(rows.discountsMarginRows, manualOverrides)}
          manualOverrides={manualOverrides}
          rows={visibleRows.visibleDiscountsMarginRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
          onToggleDetails={onToggleDetails}
          onUnapprovalChange={onUnapprovalChange}
        />
      );
    case 'egs':
      return (
        <>
          <MarginReportCard
            expandedRows={expandedQueueItems}
            hiddenApprovedCount={getHiddenApprovedCount(rows.marginRows, manualOverrides)}
            manualOverrides={manualOverrides}
            rows={visibleRows.visibleMarginRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
            onToggleDetails={onToggleDetails}
            onUnapprovalChange={onUnapprovalChange}
          />

          {rows.capacityRows.length > 0 && (
            <CapacityReportCard
              hiddenApprovedCount={getHiddenApprovedCount(rows.capacityRows, manualOverrides)}
              manualOverrides={manualOverrides}
              rows={visibleRows.visibleCapacityRows}
              showAutoApproved={showAutoApproved}
              onApprovalChange={onApprovalChange}
              onUnapprovalChange={onUnapprovalChange}
            />
          )}
        </>
      );
    case 'dealOptionsInclusions':
      return (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load deal options or inclusions data — some rules may show as missing data.
            </Alert>
          )}
          <DealOptionsInclusionsReportCard
            dealRows={dealOptionsInclusionsDealRows}
            expandedRows={expandedQueueItems}
            hiddenApprovedCount={getHiddenApprovedCount(rows.dealOptionsInclusionsRows, manualOverrides)}
            manualOverrides={manualOverrides}
            searchQuery={searchQuery}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
            onToggleDetails={onToggleDetails}
            onUnapprovalChange={onUnapprovalChange}
          />
        </>
      );
    case 'surcharges':
      return (
        <SurchargesReportCard
          expandedRows={expandedQueueItems}
          flaggedRules={rows.baseSurchargesRows}
          hiddenApprovedCount={getHiddenApprovedCount(rows.surchargeTableRows, manualOverrides)}
          manualOverrides={manualOverrides}
          rows={visibleRows.visibleSurchargeTableRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
          onToggleDetails={onToggleDetails}
          onUnapprovalChange={onUnapprovalChange}
        />
      );
    case 'blackoutDates':
      return (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity blackout dates — affected rules require approval.
            </Alert>
          )}
          <BlackoutReportCard
            coverage={blackoutCoverage}
            coverageRow={rows.blackoutRows.find((row) => row.field === 'Blackout Coverage')}
            hiddenApprovedCount={
              rows.blackoutRows.filter((row) => row.field === 'Blackout Dates' && isRowApproved(row, manualOverrides))
                .length
            }
            manualOverrides={manualOverrides}
            rows={visibleRows.visibleBlackoutRows.filter((row) => row.field === 'Blackout Dates')}
            showAutoApproved={showAutoApproved}
            travelFrom={travelFrom}
            travelTo={travelTo}
            onApprovalChange={onApprovalChange}
            onUnapprovalChange={onUnapprovalChange}
          />
        </>
      );
    case 'bundledExperiences':
      return (
        <BundledExperiencesReportCard
          expandedRows={expandedQueueItems}
          hiddenApprovedCount={getHiddenApprovedCount(rows.bundledExperienceRows, manualOverrides)}
          manualOverrides={manualOverrides}
          rows={visibleRows.visibleBundledExperienceRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={onApprovalChange}
          onToggleDetails={onToggleDetails}
          onUnapprovalChange={onUnapprovalChange}
        />
      );
    case 'taxesPayments':
      return (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity data — some rules may show as missing data.
            </Alert>
          )}
          <TaxesPaymentsReportCard
            expandedRows={expandedQueueItems}
            hiddenApprovedCount={getHiddenApprovedCount(rows.taxesPaymentsRows, manualOverrides)}
            manualOverrides={manualOverrides}
            rows={visibleRows.visibleTaxesPaymentsRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={onApprovalChange}
            onToggleDetails={onToggleDetails}
            onUnapprovalChange={onUnapprovalChange}
          />
        </>
      );
    default:
      return <PlaceholderChecklistTab category={activeCategory} />;
  }
}
